/**
 * Owner details block — name, address, contact info.
 * Exports both a react-pdf version (for PDF generation) and an HTML version (for screen preview).
 */
import { StyleSheet, Text, View } from '@react-pdf/renderer'

import type { Member } from '@snootsvet/shared'

export interface OwnerDetailsProps {
  owner: Member
  /** Whether to show the address line. Defaults to true. */
  showAddress?: boolean
  /** Whether to show contact details (email/phone). Defaults to true. */
  showContact?: boolean
}

/** Format a member's address from its component parts. */
function formatAddress(owner: Member): string {
  return [owner.address1, owner.address2, owner.city, owner.postalCode].filter(Boolean).join(', ')
}

/** Format owner contact details into a single string. */
function formatContact(owner: Member): string {
  return [owner.email, owner.phone].filter(Boolean).join(' / ')
}

export function OwnerDetailsPreview({
  owner,
  showAddress = true,
  showContact = true,
}: OwnerDetailsProps) {
  const address = showAddress ? formatAddress(owner) : null
  const contact = showContact ? formatContact(owner) : null

  return (
    <div className="leading-snug">
      <p>
        <span className="font-semibold">Owner: </span>
        {owner.firstName} {owner.lastName}
      </p>
      {address && (
        <p>
          <span className="font-semibold">Address: </span>
          {address}
        </p>
      )}
      {contact && (
        <p>
          <span className="font-semibold">Contact details: </span>
          {contact}
        </p>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  label: { fontFamily: 'Helvetica-Bold', fontSize: 10 },
  text: { fontSize: 10, marginBottom: 1 },
})

export function OwnerDetails({ owner, showAddress = true, showContact = true }: OwnerDetailsProps) {
  const address = showAddress ? formatAddress(owner) : null
  const contact = showContact ? formatContact(owner) : null

  return (
    <View wrap={false}>
      <Text style={styles.text}>
        <Text style={styles.label}>Owner: </Text>
        {owner.firstName} {owner.lastName}
      </Text>
      {address && (
        <Text style={styles.text}>
          <Text style={styles.label}>Address: </Text>
          {address}
        </Text>
      )}
      {contact && (
        <Text style={styles.text}>
          <Text style={styles.label}>Contact details: </Text>
          {contact}
        </Text>
      )}
    </View>
  )
}
